/**
 * 149-cw (PROMPT 16-AUTH-ADMIN-MEDIATED-P0): Password reset request queue.
 * BOD LV1 only. Read-only listing + approve/reject/cancel/regenerate actions.
 */
import React, { useEffect, useState } from "react";
import client from "../lib/api";

const STATUS_STYLE = {
  REQUESTED: "bg-amber-100 text-amber-800 border-amber-300",
  APPROVED: "bg-sky-100 text-sky-800 border-sky-300",
  LINK_ACTIVE: "bg-emerald-100 text-emerald-800 border-emerald-300",
  COMPLETED: "bg-slate-100 text-slate-700 border-slate-300",
  EXPIRED: "bg-slate-100 text-slate-500 border-slate-300",
  REJECTED: "bg-rose-100 text-rose-800 border-rose-300",
  CANCELLED: "bg-slate-100 text-slate-500 border-slate-300",
};

export default function PasswordResetRequestsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [linkResult, setLinkResult] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => {
    const q = filter ? `?status=${filter}` : "";
    client.get(`/admin/password-reset-requests${q}`)
      .then((r) => { setItems(r.data.items); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || "Gagal memuat antrean"));
  };
  useEffect(load, [filter]);

  const approve = async () => {
    if (!selected) return;
    if (note.trim().length < 10) { alert("Catatan verifikasi minimal 10 karakter."); return; }
    try {
      const { data } = await client.post(
        `/admin/password-reset-requests/${selected.id}/approve-and-generate`,
        { verification_note: note });
      setLinkResult(data);
      setNote("");
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal approve"); }
  };

  const reject = async () => {
    if (!selected) return;
    if (reason.trim().length < 5) { alert("Alasan minimal 5 karakter."); return; }
    try {
      await client.post(`/admin/password-reset-requests/${selected.id}/reject`, { reason });
      setReason(""); setSelected(null); load();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal reject"); }
  };

  const cancel = async () => {
    if (!selected) return;
    try {
      await client.post(`/admin/password-reset-requests/${selected.id}/cancel`,
        { reason: reason || null });
      setReason(""); setSelected(null); load();
    } catch (e) { alert(e?.response?.data?.detail || "Gagal cancel"); }
  };

  if (err) return <div className="p-8 text-rose-800" data-testid="prq-error">{err}</div>;

  return (
    <div className="p-6 space-y-4" data-testid="prq-page">
      <header>
        <div className="text-xs uppercase tracking-widest text-slate-500">User Management</div>
        <h1 className="text-2xl font-bold text-slate-900">Permintaan Reset Password</h1>
        <p className="text-sm text-slate-600 mt-1">
          Verifikasi identitas user melalui saluran resmi <b>SEBELUM</b> menyetujui.
          Email awal ke admin tidak berisi token. Link hanya sekali pakai dan berlaku 30 menit.
        </p>
      </header>

      <div className="flex gap-2 items-center">
        <label className="text-xs">Filter status:</label>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          className="text-sm border border-slate-300 rounded px-2 py-1"
          data-testid="prq-filter">
          <option value="">Semua</option>
          {["REQUESTED","APPROVED","LINK_ACTIVE","COMPLETED","EXPIRED","REJECTED","CANCELLED"].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button onClick={load} className="text-xs text-slate-600 underline">Refresh</button>
        <span className="ml-auto text-xs text-slate-500">{items.length} record</span>
      </div>

      <table className="w-full text-xs border border-slate-200 bg-white">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            <th className="p-2 text-left">Request ID</th>
            <th className="p-2 text-left">User</th>
            <th className="p-2 text-left">Divisi</th>
            <th className="p-2 text-left">Requested</th>
            <th className="p-2 text-left">Status</th>
            <th className="p-2 text-left">Expires</th>
            <th className="p-2 text-left">Processed by</th>
            <th className="p-2 text-left">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-t border-slate-200"
                data-testid={`prq-row-${r.id.slice(0,8)}`}>
              <td className="p-2 font-mono">{r.id.slice(0,8)}…</td>
              <td className="p-2"><div><b>{r.user_name}</b></div>
                <div className="text-slate-500 text-xs">{r.user_email_masked}</div></td>
              <td className="p-2">{r.user_division} L{r.user_level}</td>
              <td className="p-2">{r.requested_at?.slice(0,16).replace("T"," ")}</td>
              <td className="p-2"><span className={`px-2 py-0.5 rounded border ${STATUS_STYLE[r.status] || ""}`}>{r.status}</span></td>
              <td className="p-2">{r.token_expires_at?.slice(0,16).replace("T"," ") || "-"}</td>
              <td className="p-2">{r.processed_by_name || "-"}</td>
              <td className="p-2">
                <button onClick={() => { setSelected(r); setLinkResult(null); }}
                        className="text-xs text-sky-700 underline"
                        data-testid={`prq-open-${r.id.slice(0,8)}`}>Buka</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan="8" className="p-6 text-center text-slate-500">Tidak ada permintaan.</td></tr>
          )}
        </tbody>
      </table>

      {selected && (
        <div className="border border-slate-300 rounded p-4 bg-slate-50 space-y-3"
              data-testid="prq-detail">
          <h3 className="font-semibold">Detail: {selected.user_name} — {selected.user_email_masked}</h3>
          <div className="text-xs text-slate-600">Request ID: <code>{selected.id}</code></div>
          <div className="text-xs">Status saat ini: <b>{selected.status}</b></div>
          {selected.verification_note && (
            <div className="text-xs text-slate-700">Catatan verifikasi: {selected.verification_note}</div>
          )}
          {["REQUESTED","APPROVED","LINK_ACTIVE"].includes(selected.status) && (
            <>
              <div>
                <label className="text-xs font-medium">Catatan Verifikasi (min 10 karakter, wajib) <span className="text-rose-600">*</span></label>
                <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                  className="w-full border border-slate-300 rounded p-2 text-xs"
                  placeholder="Contoh: Verifikasi via WA kantor 0812xxxxxxxx pkl 10:30, dikonfirmasi atasan langsung"
                  data-testid="prq-verification-note" />
                <button onClick={approve}
                        className="mt-2 px-3 py-1 bg-emerald-700 text-white text-xs rounded"
                        data-testid="prq-approve">Setujui & Buat Link Reset</button>
              </div>
              <div>
                <label className="text-xs">Alasan (untuk reject/cancel)</label>
                <input value={reason} onChange={(e) => setReason(e.target.value)}
                  className="w-full border border-slate-300 rounded p-2 text-xs" />
                <div className="flex gap-2 mt-2">
                  <button onClick={reject} className="px-3 py-1 bg-rose-700 text-white text-xs rounded"
                          data-testid="prq-reject">Tolak</button>
                  <button onClick={cancel} className="px-3 py-1 bg-slate-500 text-white text-xs rounded"
                          data-testid="prq-cancel">Batalkan</button>
                </div>
              </div>
            </>
          )}
          {linkResult && (
            <div className="border border-emerald-300 bg-emerald-50 rounded p-3"
                 data-testid="prq-link-result">
              <div className="text-xs font-semibold text-emerald-800">Link Reset Berhasil Dibuat</div>
              <div className="text-xs text-slate-700 mt-1">Berlaku sampai: <b>{linkResult.token_expires_at?.slice(0,16).replace("T"," ")}</b></div>
              <div className="text-xs text-slate-700 mt-1 break-all font-mono border border-slate-300 bg-white p-2 rounded"
                   data-testid="prq-link">{linkResult.reset_url}</div>
              <button onClick={() => navigator.clipboard.writeText(linkResult.reset_url)}
                className="mt-2 px-3 py-1 bg-sky-700 text-white text-xs rounded"
                data-testid="prq-copy">Salin Link Reset</button>
              <p className="text-xs italic text-slate-600 mt-2">
                Serahkan link ini ke user melalui saluran yang diverifikasi (bukan email publik).
                Link hanya sekali pakai, akan kadaluarsa otomatis, dan tidak akan disimpan permanen.
              </p>
            </div>
          )}
          <button onClick={() => { setSelected(null); setLinkResult(null); setNote(""); setReason(""); }}
            className="text-xs text-slate-500 underline">Tutup</button>
        </div>
      )}
    </div>
  );
}
